import { fetchWithTimeout } from '@noths/polaris-client-utils';

import { PUBLIC_API_BASE_PATH } from 'src/constants/environment';
import type { TaxonomyContentEntry } from 'src/redux/content/types';
import { logger } from 'src/utils/serverLogger';
import { encodePathQueryParam } from './encodePathQueryParam';

/**
 * Bounds the request rather than leaving it open indefinitely. `syncTaxonomyContentFromURL`
 * holds `content.syncing` for the whole call, so a hung request would keep the
 * header in its syncing hold forever and keep a browser connection open against
 * a pod that may already be under pressure. The route behind this fetches from
 * Contentstack at `TAXONOMY_PLP_CONTENT_FETCH_TIMEOUT_MS` (1s), so 2s leaves
 * headroom over the server's own budget.
 */
export const TIMEOUT = 2000;

/** Client-side fetch of a single taxonomy_plp content entry via the public API route. */
export const fetchTaxonomyPlpContentEntry = async (
  path: string,
): Promise<TaxonomyContentEntry | null> => {
  try {
    const res = await fetchWithTimeout(
      `${PUBLIC_API_BASE_PATH}taxonomy-plp-content?path=${encodePathQueryParam(path)}`,
      { timeout: TIMEOUT },
    );

    if (!res.ok) {
      logger.error('fetchTaxonomyPlpContentEntry: non-ok response', { path, status: res.status });

      return null;
    }

    const data = (await res.json()) as { content?: TaxonomyContentEntry | null };

    return data.content ?? null;
  } catch (error) {
    logger.error('fetchTaxonomyPlpContentEntry: request failed', {
      path,
      message: error instanceof Error ? error.message : String(error),
    });

    return null;
  }
};
